"use client";

import { useFeatureFlagEnabled, usePostHog } from "posthog-js/react";
import { useRef } from "react";
import { buttonClass } from "@/components/ui/button";
import { useViewOnce } from "@/lib/analytics/use-view-once";

/** Sole switch for this link: ENABLED = renders, absent/DISABLED = today's page. */
export const JOBDETAIL_EMPLOYER_LINK_FLAG = "jobdetail-employer-brief-link";

/**
 * Sole switch for the role-aware target (card #7555 follow-up): ENABLED and the
 * job maps to a hire page = the CTA opens `/employers/hire/<role>` and the copy
 * names the role; absent/DISABLED, or no mapped role = today's generic hub link.
 */
export const JOBDETAIL_EMPLOYER_ROLE_LINK_FLAG = "jobdetail-employer-role-link";

export type JobDetailHireRole = {
	slug: string;
	noun: { en: string; id: string };
};

/**
 * Distribution into the employer hub from the job detail page (~645 human
 * persons/7d vs ~20/7d on the hub). Offers the free brief to a hiring visitor;
 * seekers are never asked to pay. Client island because the page is
 * edge-cached. Off/absent renders nothing.
 */
export function JobDetailEmployerLink({
	locale,
	hireRole,
}: {
	locale: "id" | "en";
	hireRole?: JobDetailHireRole | null;
}) {
	const enabled = useFeatureFlagEnabled(JOBDETAIL_EMPLOYER_LINK_FLAG) === true;
	const roleFlag =
		useFeatureFlagEnabled(JOBDETAIL_EMPLOYER_ROLE_LINK_FLAG) === true;
	const role = roleFlag && hireRole ? hireRole : null;
	const posthog = usePostHog();
	const sectionRef = useRef<HTMLElement>(null);
	const isId = locale === "id";

	// Impression, not mount: the link sits below the Apply block (0 clicks on 376 mounts/7d).
	useViewOnce(sectionRef, enabled, () => {
		posthog?.capture("jobdetail_employer_link_viewed", {
			locale,
			...(role ? { role_link: true, hire_slug: role.slug } : {}),
		});
	});

	if (!enabled) return null;

	return (
		<section
			ref={sectionRef}
			aria-label={isId ? "Untuk perekrut" : "For employers"}
			data-testid="jobdetail-employer-link"
			className="mt-6 flex flex-col gap-3 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between"
		>
			<p className="text-sm text-foreground">
				{role
					? isId
						? `Sedang mencari ${role.noun.id}? Lihat kisaran gaji dan kirim brief gratis, kami cocokkan dengan kandidat dari pool kami.`
						: `Hiring ${role.noun.en}? See the salary range and send a free brief, and we will match it to candidates from our pool.`
					: isId
						? "Sedang merekrut posisi serupa? Kirim brief gratis dan kami cocokkan dengan kandidat dari pool kami."
						: "Hiring for a similar role? Send a free brief and we will match it to candidates from our pool."}
			</p>
			<a
				href={
					role
						? `/${locale}/employers/hire/${role.slug}?src=job_detail&utm_source=job_detail&utm_medium=employer_link`
						: `/${locale}/employers?src=job_detail#start`
				}
				data-testid="jobdetail-employer-cta"
				onClick={() =>
					posthog?.capture("jobdetail_employer_link_clicked", {
						locale,
						...(role ? { role_link: true, hire_slug: role.slug } : {}),
					})
				}
				className={buttonClass({ size: "sm", variant: "secondary" })}
			>
				{isId ? "Isi brief gratis" : "Send a free brief"}
			</a>
		</section>
	);
}
